"use client";

import styles from "@/styles/cart/index.module.scss";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Input, Checkbox, Skeleton, Button } from "react-vant";
import { ArrowDown, Warning } from "@react-vant/icons";
import GoodsSelectItem from "@/components/cart/GoodsSelectItem";
import Settlement from "@/components/cart/Settlement";
import EmptyCart from "@/components/cart/EmptyCart";
import Bdotd from "@/components/home/Bdotd";
import { toast } from "@/components/global/Toast";
import Dialog from "@/components/global/Dialog";
import viewcartApi from "@/service/modules/viewcartApi";
import loginApi from "@/service/modules/loginApi";
import myAccountApi from "@/service/modules/myAccountApi";
import paymentApi from "@/service/modules/paymentApi";
import { getCookie } from "@/utils/cookie";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { updateCartList } from "@/store/slices/cartListSlice";
import ExpediteDialog from "@/components/global/ExpediteDialog";
import type { CartProduct, DiscountInfo } from "@/types/viewCart";
import SuburbSelectDropdown from "@/components/global/SuburbSelectDropdown";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import PageTheme from "@/components/global/PageTheme";
import {
  gaAddToCart,
  gaRemoveFromCart,
  gaViewCart,
  type GAItem,
} from "@/utils/gaEvent";

interface LoadingRows {
  [key: number]: boolean;
}

// 计算已经没有选中的邮费
let currentBatchID: string | null = null;

const cartProductToGAItem = (
  product: CartProduct,
  quantity: number = product.quantity,
): GAItem => ({
  item_id: product.productID,
  item_name: product.productName,
  price: Number(product.price) || 0,
  quantity: Number(quantity) || 1,
  ...(product.productCode ? { item_variant: product.productCode } : {}),
  google_business_vertical: "retail",
});

export default function Cart() {
  const dispatch = useAppDispatch();
  const hasTrackedCartPage = useRef(false);
  const hasTrackedViewCart = useRef(false);
  const [effectiveList, setEffectiveList] = useState<CartProduct[]>([]);
  const [invalidityList, setInvalidity] = useState<CartProduct[]>([]);
  const [isFirst, setIsFirst] = useState(true);
  const [showCoupon, setShowCoupon] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteArray, setDeleteArray] = useState<React.Key[]>([]);
  const [loadingRows, setLoadingRows] = useState<LoadingRows>({});
  const userInfo = useAppSelector(
    (state) => state.user.customerInfo
  );

  const defaultSuburbID = useSelector((state: RootState) => state.user.customerInfo.suburbID);
  const defaultSuburbName = useSelector((state: RootState) => state.user.customerInfo.suburb);
  const [suburbId, setSuburbId] = useState<any>("")
  const [suburbName, setSuburbName] = useState<string>("")
  const [inputFocus, setInputFocus] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [credits, setCredits] = useState(0);
  const [points, setPoints] = useState(0);
  const [creditsInput, setCreditsInput]: any = useState("");
  const [pointsInput, setPointsInput]: any = useState("");
  const [coupon, setCoupon]: any = useState();
  const [applyBtnLoading, setApplyBtnLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>({});
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo>({
    couponDiscountValue: 0,
    credits: 0,
    points: 0,
    pointsValue: 0,
  });
  const [isLogin, setIsLogin] = useState(false);
  const [affiliateid, setAffiliateid] = useState<string | undefined>("");
  // 配置一个Uleeid 用于判断 不可用场景
  const [isUleeId, setIsUleeId] = useState(false);
  const [couponPackage, setCouponPackage] = useState<number>(0);
  const [packagesList, setPackagesList] = useState<any>([]);

  const [afterPayJs, setAfterPayJs] = useState("");
  const [afterPayJsLoaded, setAfterPayJsLoaded] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [selectNum, setSelectNum] = useState(0);

  // 初始化读取cookie中的affiliateid值
  useEffect(() => {
    const initialAffiliateId = getCookie("affiliateid");
    setAffiliateid(initialAffiliateId);
    setIsUleeId(
      affiliateid == "23" ||
        affiliateid == "1002" ||
        affiliateid == "1" ||
        affiliateid == "19" ||
        affiliateid == "21"
    );
    // 定期检查cookie中的affiliateid值的变化
    const checkCookie = setInterval(() => {
      const currentAffiliateId = getCookie("affiliateid");
      if (currentAffiliateId !== affiliateid) {
        setAffiliateid(currentAffiliateId);
      }
    }, 1000); // 每秒检查一次
    // 清理定时器
    return () => clearInterval(checkCookie);
  }, [affiliateid]);

  // 先粗略判断是否登录
  const checkIsLogin = async () => {
    try {
      const res = await loginApi.me();
      setIsLogin(!!res.data.customerID);
      return !!res.data.customerID;
    } catch {
      return false;
    }
  };


  const getList = async () => {
    try {
      setLoading(true);
      const res = await viewcartApi.getList();
      setLoading(false);
      setIsFirst(false);

      setPageDate(res);

      // 等待列表数据, 推送到Emarsys
      if (res.data && res.data.list && res.data.list.available) {
        // pushScarabQueueEvent(res.data.list.available || [])
      }

      // 将获取的数据同步到 Redux
      dispatch(updateCartList(res));
    } catch (error: any) {
      toast.show(error.message);
      setLoading(false);
      setIsFirst(false);
    }
  };

  const setPageDate = async (res: any) => {
    const {
      data: {
        list: { available, unavailable },
        count,
      },
    } = res;
    const loadRows: LoadingRows = {};
    const rowKeys: Array<number> = [];
    const unSelectList: Array<any> = [];
    const batchID = generateRandomID();
    currentBatchID = batchID;

    setSelectNum(count);
    if (available && available.length) {
      setEffectiveList(available);
      if (!hasTrackedViewCart.current) {
        gaViewCart({
          currency: "NZD",
          value: available.reduce(
            (sum: number, product: CartProduct) =>
              sum + Number(product.price || 0) * Number(product.quantity || 0),
            0,
          ),
          items: available.map((product: CartProduct) =>
            cartProductToGAItem(product),
          ),
        });
        hasTrackedViewCart.current = true;
      }
      available.forEach((v: any) => {
        // 给所有有效列表的行加上邮费 loading状态
        loadRows[v.productID] = true;

        // 过滤出选中列表
        if (v.appSelectStatus == 1) {
          rowKeys.push(v.productID);
        } else {
          // 过滤出未选中的列表
          unSelectList.push(v);
        }
      });
    } else {
      setEffectiveList([]);
    }
    if (unavailable && unavailable.length) {
      setInvalidity(unavailable);
    } else {
      setInvalidity([]);
    }
    setSelectedRowKeys(rowKeys);
    setLoadingRows(loadRows);

    window.scrollTo({
      top: 0,
      behavior: "smooth", // 添加平滑滚动效果
    });

    // 计算已经选中的邮费，算完后马上计算没有选中的邮费
    if (rowKeys.length) {
      // 登录获取优惠券
      const loginStatus = await checkIsLogin();
      if (loginStatus) {
        await getCouponsList();
      } else {
        await calculateSelectPost();
      }
    } else {
      setPaymentInfo({});
      setDiscountInfo({
        couponDiscountValue: 0,
        credits: 0,
        points: 0,
        pointsValue: 0,
      });
      setPackagesList([]);
      setCouponPackage(0);
      setCoupon("");
    }
    if (unSelectList.length) {
      calculateUnSelectPost(unSelectList, batchID);
    }
  };

  // 计算已经选中的邮费
  const calculateSelectPost = async () => {
    try {
      const res = await viewcartApi.calculateSelectPost({ suburb: suburbName, suburbID: suburbId })
      setCalculateData(res)
      const { data: { cart } } = res
      if (cart.discountInfo && cart.discountInfo.message) {
        // message.error(cart.discountInfo.message)
        setCouponPackage(0)
        setCoupon("")
      }
    } catch (error: any) {
      if (error.message) {
        toast.show(error.message);
      }
    }
  }

  const setCalculateData = (response: any) => {
    const {
      data: {
        cart: { items },
        cart,
      },
    } = response;
    if (!(cart && items)) return;
    setPostTable(items);
    setPaymentInfo(cart);

    if (cart.discountInfo && cart.discountInfo.discountTotal) {
      setDiscountInfo(cart.discountInfo || {});
      setShowCoupon(true);
    } else {
      setDiscountInfo({
        couponDiscountValue: 0,
        credits: 0,
        points: 0,
        pointsValue: 0,
      });
    }

    if (cart.discountInfo && cart.discountInfo.points) {
      setPointsInput(cart.discountInfo.points);
    } else {
      setPointsInput("");
    }

    if (cart.discountInfo && cart.discountInfo.credits) {
      setCreditsInput(cart.discountInfo.credits);
    } else {
      setCreditsInput("");
    }

    if (cart.discountInfo && cart.discountInfo.packageID > 0) {
      setCouponPackage(cart.discountInfo.packageID);
    } else {
      setCouponPackage(0);
    }

    // 设置 Tag Assistant Legacy data
    const ecomm_prodid = []
    for (const key in items) {
      if (Object.prototype.hasOwnProperty.call(items, key)) {
        const element = items[key];
        ecomm_prodid.push(element.productID)
      }
    }
    const porductId = ecomm_prodid.join(",")
    const g_params = {
      ...(!hasTrackedCartPage.current ? { event: "shoppingCartPage" } : {}),
      "remarketing_params": {
        "ecomm_prodid": ecomm_prodid,
        "ecomm_pagetype": "cart",
        "ecomm_totalvalue": cart.subTotal - cart.discount,
        "ecomm_prodname": "",
        "ecomm_catname": "",
        "ecomm_email": ""
      },
      "params": {
        "userId": userInfo?.customerID,
        "categoryId": null,
        "productId": porductId
      }
    }
    setGtmDataLayer(g_params)
    hasTrackedCartPage.current = true;
  };

  const generateRandomID = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  useEffect(() => {
    if (defaultSuburbID) {
      setSuburbId(parseInt(`${defaultSuburbID}`))
      setSuburbName(defaultSuburbName)
    }
  }, [defaultSuburbID])

  useEffect(() => {
    if (suburbId) {
      getList()
    }
  }, [suburbId])

  const suburbChange = (data: any, option: any) => {
    setSuburbId(data)
    if (option.name) setSuburbName(option.name)
  }

  const calculateUnSelectPost = async (
    unavailableList: Array<any>,
    batchID: any
  ) => {
    if (unavailableList.length === 0) return;

    const selectNum = 10;

    const processBatch = async (batch: Array<any>, batchID: string) => {
      if (batchID !== currentBatchID) {
        // If the batch ID does not match the current batch ID, exit the function
        return;
      }

      const firstTen = batch.map((v: any) => ({
        productID: v.productID,
        quantity: v.quantity
      }));

      const params = {
        suburb: suburbName,
        items: firstTen,
        suburbID: suburbId
      };

      try {
        const res = await viewcartApi.calcSingle(params);
        const { data: { cart: { items } } } = res;
        setPostTable(items);
      } catch (error: any) {
        toast.show(error.message);
      }
    };

    const batches = [];

    while (unavailableList.length) {
      batches.push(unavailableList.splice(0, selectNum));
    }

    for (const batch of batches) {
      await processBatch(batch, batchID);
    }
  };

  // 写回真实邮费到table中
  const setPostTable = (postItems: any) => {
    const map: any = postItems;
    const newLoadRows: LoadingRows = {};
    for (const key in postItems as Record<number, any>) {
      if (Object.prototype.hasOwnProperty.call(postItems, key)) {
        newLoadRows[key] = false;
      }
    }

    // 写回到 table中
    setEffectiveList((invalidity: any) =>
      invalidity.map((item: any) => ({
        ...item,
        ...map[item.productID],
      }))
    );
    setLoadingRows((loadRows: LoadingRows) => ({
      ...loadRows,
      ...newLoadRows,
    }));
  };

  const applyDiscount = async () => {
     try {
      if (selectedRowKeys.length < 1) {
        toast.show("Please select items！");
        return;
      }
      if (couponPackage < 1 && !coupon && pointsInput < 0 && creditsInput < 0) {
        toast.show('Please use your credits, BestDealsPoints, or coupon code to apply the discount.')
        return
      }
      setApplyBtnLoading(true)
      const params = {
        "coupon": couponPackage > 0 ? '' : coupon,
        "credits": creditsInput ? creditsInput : 0,
        "points": pointsInput ? pointsInput : 0,
        "suburbID": suburbId,
        "suburb": suburbName,
        'packageID': couponPackage > 0 ? couponPackage : 0
      }
      const res = await viewcartApi.applyDiscount(params)
      setApplyBtnLoading(false)
      setCalculateData(res)
      const { data: { cart } } = res
      if (cart.discountInfo && cart.discountInfo.message) {
        toast.show(cart.discountInfo.message)
        setCouponPackage(0)
        setCoupon("")
      }
    } catch (error: any) {
      toast.show(error.message)
      setApplyBtnLoading(false)
    }
  };

  // 用于第一次加载页面时，使用优惠券
  const usedCoupon = async ({ id }: any) => {
    const params = {
      "coupon": '',
      "credits": 0,
      "points": 0,
      "suburb": suburbName,
      'packageID': id
    }
    const res = await viewcartApi.applyDiscount(params)
    const { data: { cart } } = res
    setApplyBtnLoading(false)
    setCalculateData(res)


    if (cart.discountInfo && cart.discountInfo.message) {
      toast.show(cart.discountInfo.message)
      setCouponPackage(0)
      setCoupon("")
    }
  }

  const compareCart = async () => {
    try {
      if (compareLoading) return false;
      setCompareLoading(true);
      const invalidity = effectiveList
        .filter((v: any) => v.appSelectStatus == 1)
        .map((v: any) => ({
          pid: v.productID,
          qty: v.quantity,
          upgrade: v.upgradeExpress,
          selected: v.appSelectStatus,
        }));
      const diffData = await viewcartApi.compareCart({ items: invalidity });
      setCompareLoading(false);
      const {
        data: { isSame },
      } = diffData;
      if (!isSame) {
        // setLoading(true)
        updataCartList();
        throw new Error(
          "Your shopping cart has been updated due to changes made in another tab. The page will refresh to reflect the latest data."
        );
      }
      return isSame;
    } catch {
      setCompareLoading(false);
    }
  };

  // 快速本地更新购物车
  const updateData = (productID: any, newData: any) => {
    const updatedData: any = effectiveList.map((item: any) => {
      if (item.productID === productID) {
        return { ...item, ...newData };
      }
      return item;
    });
    setEffectiveList(updatedData);
  };

  // 更新购物车列表
  const updataCartList = async () => {
    if (suburbId) {
      getList()
    }
  };

  // 更新购物车（数量、upgrade、select）
  const updateCart = async (type: string | number, value: any, items: any) => {
    try {
      if (loading) return false;
      const { productID, appSelectStatus, upgradeExpress, quantity } = items;
      // 快速更新购物车数据
      switch (type) {
        case "qty":
          updateData(items.productID, { quantity: value });
          break;
        case "upgrade":
          updateData(items.productID, { upgradeExpress: value ? 1 : 0 });
          break;
        case "selected":
          setSelectedRowKeys((prev: any) =>
            value
              ? [...prev, productID]
              : prev.filter((item: any) => item !== productID)
          );
          break;
      }

      const isSame = await compareCart();
      if (!isSame) return;
      setLoading(true);

      const record = {
        pid: productID,
        qty: quantity,
        upgrade: upgradeExpress,
        selected: appSelectStatus,
      };

      switch (type) {
        case "qty":
          record.qty = parseInt(value);
          break;
        case "upgrade":
          record.upgrade = value ? 1 : 0;
          break;
        case "selected":
          record.selected = value ? 1 : 0;
          break;
      }
      const res: any = await viewcartApi.updateCart(record);

      if (res.data) {
        if (type === "qty") {
          const quantityDelta = Number(value) - Number(quantity);
          if (quantityDelta > 0) {
            gaAddToCart("NZD", Number(items.price || 0) * quantityDelta, [
              {
                productID: items.productID,
                productName: items.productName,
                quantity: quantityDelta,
                price: Number(items.price) || 0,
                productCode: items.productCode,
              },
            ]);
          } else if (quantityDelta < 0) {
            gaRemoveFromCart({
              currency: "NZD",
              value: Number(items.price || 0) * Math.abs(quantityDelta),
              items: [cartProductToGAItem(items, Math.abs(quantityDelta))],
            });
          }
        }
        updataCartList();
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error.message) {
        toast.show(error.message);
      }
    }
  };

  // 批量删除购物车点击
  const betchDelete = (selectedRowKeys: any) => {
    if (selectedRowKeys.length === 0) {
      toast.show("Please select items!");
      return;
    }
    setDeleteArray(selectedRowKeys);
    setIsModalOpen(true);
  };

  // 删除购物车,批量删除
  const deleteCart = async () => {
    try {
      setDeleteLoading(true);
      const isSame = await compareCart();
      if (!isSame) return;

      const removedItems = [...effectiveList, ...invalidityList].filter(
        (product) => deleteArray.includes(product.productID),
      );
      await viewcartApi.deleteCart({ pids: deleteArray });
      if (removedItems.length) {
        gaRemoveFromCart({
          currency: "NZD",
          value: removedItems.reduce(
            (sum, product) =>
              sum + Number(product.price || 0) * Number(product.quantity || 0),
            0,
          ),
          items: removedItems.map((product) => cartProductToGAItem(product)),
        });
      }
      setIsModalOpen(false);
      setDeleteLoading(false);
      toast.show("Successfully!");
      updataCartList();
    } catch (error: any) {
      setIsModalOpen(false);
      setDeleteLoading(false);
      if (error.message) {
        toast.show(error.message);
      }
    }
  };

  // 下拉框选择
  const qtyChange = (record: any, value: string | number) => {
    if (Number(value) > 0) {
      updateCart("qty", value, record);
    } else {
      // 删除
      // deleteCart()
      setDeleteArray([record.productID]);
      setIsModalOpen(true);
    }
  };

  // 单独删除
  const deleteItem = (record: any) => {
    setDeleteArray([record.productID]);
    setIsModalOpen(true);
  };

  // 单独express
  const expressChange = (record: any, value: any) => {
    updateCart("upgrade", value, record);
  };

  const onSelect = (record: any, selected: boolean) => {
    updateCart("selected", selected, record);
  };

  const onSelectAll = async (selected: boolean) => {
    try {
      setLoading(true);
      await viewcartApi.selectAll({ selected: selected ? 1 : 0 });
      updataCartList();
    } catch (error: any) {
      if (error.message) {
        toast.show(error.message);
      }
      setLoading(false);
    }
  };

  const slectCoupons = async (couponPackageId: number) => {
    setCouponPackage(couponPackageId);
  };

  const couponInputChange = (value: string) => {
    setCoupon(value);
    setCouponPackage(0);
  };

  const getCreditsList = async () => {
    try {
      const res = await myAccountApi.creditsList();
      if (res.data) {
        const {
          credits: { credits, points },
        } = res.data;
        setCredits(credits);
        setPoints(points);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCouponsList = async () => {
    try {
      const res = await myAccountApi.getCouponsList();
      if (res.data) {
        const { packages } = res.data;
        setPackagesList(packages);
        if (packages.length > 0) {
          // 使用券包获取金额
          usedCoupon(packages[0]);
          setCouponPackage(packages[0].id);
        } else {
          // 计算选中的商品
          calculateSelectPost();
          setCouponPackage(0);
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (couponPackage > 0 && packagesList.length > 0) {
      const hasCoupons = packagesList.find(
        (item: any) => item.id === couponPackage
      );
      if (hasCoupons) {
        setCoupon(hasCoupons.name);
      }
    }
  }, [couponPackage, packagesList]);

  useEffect(() => {
    if (isLogin) {
      getCreditsList();
    }
  }, [isLogin]);

  // // 注入Emarsys代码
  // const RECOMMENDER_SUBDOMAIN = 'https://recommender.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';
  // const CDN_SUBDOMAIN = 'http://cdn.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';

  // // 推送到scarabQueue事件
  // function pushScarabQueueEvent(cartList: any) {
  //   if (window && (window as any).ScarabQueue) {
  //     if (localStorage.getItem('userEmail')) {
  //       (window as any).ScarabQueue.push(['setEmail', localStorage.getItem('userEmail')]);
  //     }

  //     const scarabQueueCartData = cartList.map(function (item: any) {
  //       return {
  //         item: item.productID,
  //         price: Number(item.price),
  //         quantity: Number(item.quantity)
  //       };
  //     });

  //     (window as any).ScarabQueue.push(['cart', scarabQueueCartData]);
  //     (window as any).ScarabQueue.push(['go']);
  //   } else {
  //     console.log('no window.ScarabQueue');
  //   }
  // }

  // useEffect(() => {
  //   const subdomain = document.location.protocol === 'https:' ? RECOMMENDER_SUBDOMAIN : CDN_SUBDOMAIN;
  //   const id = 'scarab-js-api';

  //   if (document.getElementById(id)) return;

  //   const js = document.createElement('script');
  //   js.id = id;
  //   js.src = subdomain;

  //   const fs = document.getElementsByTagName('script')[0];
  //   fs?.parentNode?.insertBefore(js, fs);

  //   js.onload = () => {
  //     if (effectiveList && effectiveList.length) pushScarabQueueEvent(effectiveList)
  //   }

  //   return () => {
  //     js.remove();
  //   };
  // }, []);

  // 接口获取afterpay-express-js的url
  useEffect(() => {
    const fetchAfterpayScriptUrl = async () => {
      try {
        const res = await paymentApi.getAfterPayExpressJs();
        if (res.data) setAfterPayJs(res.data.jsUrl);
      } catch (error) {
        console.error("Failed to fetch afterpay-express-js URL:", error);
      }
    };

    fetchAfterpayScriptUrl();
  }, []);

  // 注入afterpay-express-js
  useEffect(() => {
    if (afterPayJs) {
      const script = document.createElement("script");
      script.src = afterPayJs;
      script.async = true;
      script.onload = () => {
        setAfterPayJsLoaded(true);
        console.log(`Script loaded successfully: ${afterPayJs}`);
      };
      script.onerror = () => {
        console.error(`Failed to load script: ${afterPayJs}`);
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [afterPayJs]);

  // 价格变化，重新初始化afterpay-express
  useEffect(() => {
    if (
      afterPayJsLoaded &&
      paymentInfo &&
      paymentInfo.total > 0 &&
      paymentInfo.total < 4000
    ) {
      handleInitAfterpay();
    }
  }, [afterPayJsLoaded, paymentInfo]);

  // 初始化afterpay-express
  const handleInitAfterpay = async () => {
    if (typeof (window as any).AfterPay == "undefined") {
      console.log("can not use afterpay");
      return;
    }

    (window as any).AfterPay.initializeForPopup({
      countryCode: "NZ",
      target: "#afterpayBtn",
      shippingOptionRequired: true,
      buyNow: true,
      onCommenceCheckout: async function (actions: any) {
        try {
          const res = await paymentApi.getAfterPayExpressToken();
          if (res.data && res.data.express) {
            actions.resolve(res.data.express.token);
          }
        } catch (error) {
          console.error("Failed to fetch script URL:", error);
        }
      },
      onComplete: async function (completeData: any) {
        if (completeData.data.status == "SUCCESS") {
          try {
            // 设置loading状态
            setPaymentLoading(true);

            const res = await paymentApi.afterpayExpressCapture({
              token: completeData.data.orderToken,
            });

            const { orderID, link, success } = res?.data || {}
            if (success === true) {
              if (orderID) {
                window.location.href = `/payment-result/after-pay-express/${orderID}`;
                return
              }
              setPaymentLoading(false);
              toast.show('Payment succeeded but order ID is missing. Please contact support.');
              return
            }
            if (link) {
              setTimeout(() => {
                window.location.href = link;
              }, 200);
              return
            }
            setPaymentLoading(false);
            toast.show('Afterpay Express Capture failed, Please try again');
          } catch {
            setPaymentLoading(false);
            toast.show("Afterpay Express Capture failed, Please try again");
          }
        } else if (
          completeData.data.status != "SUCCESS" &&
          completeData.data.status != "CANCELLED"
        ) {
          setPaymentLoading(false);
          toast.show("Afterpay Express Capture failed, Please try again");
        }
      },
      onShippingAddressChange: function (data: any, actions: any) {
        const shippingAddressInfo = {
          id: "1",
          name: "Standard Delivery",
          description: "Provided by BestDeals",
          shippingAmount: {
            amount: paymentInfo.shipping.toFixed(2).toString(),
            currency: "NZD",
          },
          taxAmount: { amount: paymentInfo.gst.toString(), currency: "NZD" },
          orderAmount: {
            amount: paymentInfo.total.toString(),
            currency: "NZD",
          },
        };

        console.log(shippingAddressInfo, ' === shippingAddressInfo');

        actions.resolve([shippingAddressInfo]);
      },
    });
  };

  // 唤起paypal express
  const handleOpenPaypal = async () => {
    try {
      if (!selectedRowKeys.length || paymentInfo.total <= 0) {
        toast.show("Please select items!");
        return;
      }

      const res = await paymentApi.getPaypalExpressUrl();
      if (res.data && res.data.link) {
        window.location.href = res.data.link;
      } else {
        if (res.data.message) toast.show(res.data.message);
      }
    } catch {
      console.error("Failed to fetch Paypal URL");
    }
  };

  // checkout点击
  const checkoutEvent = (callBack?: () => void) => {
    if (!selectedRowKeys.length) {
      toast.show("Please select items!");
      return;
    }
    callBack?.();
  };

  return (
    <div className={styles["page-view-cart"]}>
      <PageTheme footerTheme="white" />
      {!isFirst && isLogin ? <ExpediteDialog /> : null}
      {
        // 先展示骨架屏
        isFirst ? (
          <div className={styles["skeleton-box"]}>
            <Skeleton />
            <Skeleton style={{ marginTop: "28px" }} />
            <Skeleton style={{ marginTop: "28px" }} />
          </div>
        ) : effectiveList.length > 0 || invalidityList.length > 0 ? (
          <div className={styles["cart-content"]}>
            <div className={styles["page-title"]}>Shopping Cart</div>

            <div className={styles["suburb-select"]}>
              <div className={styles["suburb-select-title"]}>
                Enter and Choose Your Suburb:
              </div>
              <div className={styles["suburb-select-box"]}>
                <SuburbSelectDropdown value={suburbId} onChange={suburbChange} notAutoSelect={true} />
                <span
                  className={styles["help"]}
                  onClick={() => setShowHelp(!showHelp)}
                >
                  Help?
                </span>
              </div>

              {!!showHelp && (
                <div className={styles["suburb-help"]}>
                  {`Based on your postcode and suburb, our system will choose the most cost-effective courier. Shipping costs are subject to change depending on your address at check-out.`}
                </div>
              )}
            </div>

            <div className={styles["cart-goods-box"]}>
              <div className={styles["batch-operations-box"]}>
                <Checkbox
                  className={styles["select-all-checkbox"]}
                  shape="square"
                  iconSize="14px"
                  checked={
                    effectiveList.length > 0 &&
                    effectiveList.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0
                    ) === selectNum
                  }
                  onChange={(e) => onSelectAll(e)}
                >
                  Select all
                </Checkbox>
                
                <Image
                  className={styles["icon-delete"]}
                  alt="icon-delete"
                  src={`${process.env.IMAGE_DOMAIN}/svg/icon-delete.svg`}
                  onClick={() => betchDelete(selectedRowKeys)}
                  width={22}
                  height={22}
                />
              </div>

              <div className={styles["goods-list"]}>
                {effectiveList.map((item: CartProduct) => (
                  <GoodsSelectItem
                    product={item}
                    loading={loadingRows[item.productID]}
                    key={`${item.productID}${item.quantity}`}
                    onSelect={onSelect}
                    onDelete={deleteItem}
                    onQtyChange={(record, quantity) =>
                      qtyChange(record, quantity)
                    }
                    onExpressChange={(record, upgradeExpress) =>
                      expressChange(record, upgradeExpress)
                    }
                  ></GoodsSelectItem>
                ))}
              </div>

              {invalidityList && invalidityList.length > 0 && (
                <>
                  <div className={styles["invalidity-title"]}>
                    <Image
                      className={styles["invalidity-icon"]}
                      alt="icon"
                      src={`${process.env.IMAGE_DOMAIN}/svg/view-cart/icon-warning.svg`}
                      width={127}
                      height={127}
                    />
                    Sorry, this item has sold out
                  </div>

                  <div className={styles["invalidity-goods-list"]}>
                    {invalidityList.map(
                      (item: CartProduct, index: number) => (
                        <GoodsSelectItem
                          product={item}
                          key={index}
                          onSelect={onSelect}
                          onDelete={deleteItem}
                          onQtyChange={(record, quantity) =>
                            qtyChange(record, quantity)
                          }
                          onExpressChange={(record, upgradeExpress) =>
                            expressChange(record, upgradeExpress)
                          }
                        ></GoodsSelectItem>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {!(isUleeId && !isLogin) && (
              <div className={styles["discount-box"]}>
                <div
                  className={`${styles["discount-title"]} ${
                    showCoupon ? styles["discount-title-border"] : ""
                  }`}
                  onClick={() => setShowCoupon(!showCoupon)}
                >
                  <div className={styles["discount-title-text"]}>
                    Optional Discounts and Coupons
                  </div>
                  <ArrowDown className={styles["discount-arrow"]} />
                </div>

                {showCoupon ? (
                  <div className={styles["discount-content"]}>
                    <div
                      className={styles["coupon-tips"]}
                    > 
                      <Warning color="#faad14" fontSize={32} />
                      <span>{`Some coupons do not apply to "Best Deal of The Day and "Flash Deal" products.`}</span>
                    </div>
                    <div className={styles["coupon-input-box"]}>
                      <Input
                        className={styles["coupon-input"]}
                        value={coupon}
                        onChange={(val) => {
                          couponInputChange(String(val));
                        }}
                        onFocus={() => {
                          setInputFocus(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setInputFocus(false);
                          }, 150);
                        }}
                        placeholder={"Coupon/Voucher Code"}
                      />
                    </div>
                    {inputFocus && packagesList && packagesList.length > 0 ? (
                      <div className={styles["coupon-list"]}>
                        {packagesList.map((item: any, index: number) => (
                          <div
                            key={index}
                            className={`${styles["coupon-item"]} ${
                              couponPackage == item.id
                                ? styles["coupon-item-active"]
                                : ""
                            }`}
                            onClick={() => {
                              slectCoupons(item.id);
                            }}
                          >
                            {item.name}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {isLogin ? (
                      <div className={styles["crazy-points-box"]}>
                        <div className={styles["crazy-points-num"]}>
                          Total Credits:&nbsp;
                          <span style={{ color: "#e6415f" }}>{credits}</span>
                        </div>
                        <div className={styles["crazy-points-desc"]}>
                          (1 Credit = $1) for this purchase
                        </div>

                        <div className={styles["crazy-points-input-box"]}>
                          <Input
                            className={styles["crazy-points-input"]}
                            value={creditsInput}
                            onChange={(val) => {
                              // 只允许输入大于等于0的整数
                              const numericValue = val.replace(/[^0-9]/g, '');
                              setCreditsInput(numericValue);
                            }}
                            placeholder={
                              !isLogin
                                ? "please sign in to check your credits"
                                : ""
                            }
                            clearable
                          />
                        </div>

                        <div className={styles["crazy-points-num"]}>
                          Total BestDealPoints:&nbsp;
                          <span style={{ color: "#e6415f" }}>{points}</span>
                        </div>
                        <div className={styles["crazy-points-desc"]}>
                          1000 points = $10, minimum 1 point is required for
                          each order.
                        </div>

                        <div className={styles["crazy-points-input-box"]}>
                          <Input
                            className={styles["crazy-points-input"]}
                            value={pointsInput}
                            onChange={(val) => {
                              // 只允许输入大于等于0的整数
                              const numericValue = val.replace(/[^0-9]/g, '');
                              setPointsInput(numericValue);
                            }}
                            placeholder={
                              !isLogin
                                ? "please sign in to check your points"
                                : ""
                            }
                            clearable
                          />
                        </div>
                      </div>
                    ) : (
                      <div className={styles["crazy-points-login"]}>
                        Click&nbsp;
                        <Link
                          href={"/login?redirect=%2Fview-cart"}
                          style={{
                            color: "#e6415f",
                            textDecoration: "underline",
                          }}
                        >
                          here
                        </Link>
                        &nbsp; to use BestDealPoints or Credits.
                      </div>
                    )}

                    <Button
                      style={{ background: '#E7415F', color: '#fff' }}
                      block
                      onClick={applyDiscount}
                      loading={applyBtnLoading}
                      loadingText="Apply Now"
                    >
                      Apply Now
                    </Button>
                  </div>
                ) : null}
              </div>
            )}

            <Settlement
              selectNum={selectNum}
              paymentInfo={paymentInfo}
              discountInfo={discountInfo}
              isLogin={isLogin}
              isUleeId={isUleeId}
              setPageDate={setPageDate}
              paymentLoading={deleteLoading || paymentLoading}
              checkoutEvent={checkoutEvent}
              handleOpenPaypal={handleOpenPaypal}
            />

            <div className={styles["fixed-total-amount"]}>
              <div className={styles["fixed-content"]}>
                <Checkbox
                  className={styles["select-all-checkbox"]}
                  style={{ marginRight: "auto" }}
                  checked={
                    effectiveList.length > 0 &&
                    effectiveList.reduce(
                      (sum, item) => sum + (item.quantity || 0),
                      0
                    ) === selectNum
                  }
                  onChange={(e) => onSelectAll(e)}
                >
                  Select all
                </Checkbox>

                <div className={styles["total-amount-box"]}>
                  <div className={styles["total-amount-title"]}>
                    Total Amount:
                  </div>
                  {typeof paymentInfo.total === "number" &&
                  !isNaN(paymentInfo.total) &&
                  paymentInfo.total > 0 ? (
                    <div className={styles["total-amount-number"]}>
                      ${paymentInfo.total.toFixed(2) || "0.00"}
                    </div>
                  ) : (
                    <div className={styles["total-amount-number"]}>
                      ${"0.00"}
                    </div>
                  )}
                </div>

                <Button
                  type="primary"
                  loading={deleteLoading}
                  loadingText={`Checkout(${selectNum})`}
                  disabled={Boolean(!selectNum)}
                  className={styles["checkout-btn"]}
                  onClick={() => {
                    checkoutEvent(() => {
                      window.location.href = `/checkout${
                        isLogin ? "" : "/express"
                      }`;
                    });
                  }}
                >
                  {`Checkout(${selectNum})`}
                </Button>
              </div>
            </div>

            <Dialog
              visible={isModalOpen}
              closeable
              title="Delete item"
              bodyClassName={styles["delete-dialog-body"]}
              showConfirmButton={true}
              showCancelButton={true}
              confirmButtonText="Yes"
              cancelButtonText="No"
              onCancel={() => setIsModalOpen(false)}
              onConfirm={deleteCart}
              confirmLoading={deleteLoading}
            >
              <p>
                Are you sure you want to delete
                <br />
                the selected items?
              </p>
            </Dialog>
          </div>
        ) : (
          <>
            <EmptyCart />
            <div className={styles["csotd-box"]}>
              <Bdotd setPageDate={setPageDate} />
            </div>
          </>
        )
      }
    </div>
  );
}
